import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface ReportSettingsConfig {
  showAgentDashboard: boolean;
  allowAgentThresholds: boolean;
  allowAgentDashboards: boolean;
  allowSharedDashboards: boolean;
  showAdherenceToAgents: boolean;
}

interface SubscriptionFlags {
  enableCustomThresholds: boolean;
  enableAgentDashboard: boolean;
  enableScheduleAdherenceReports: boolean;
  enableCustomDashboards: boolean;
  maxCustomDashboards: number;
  enableConversationReports: boolean;
  enableMissedChatReports: boolean;
  enableSentimentReports: boolean;
  enableAgentPerformanceReports: boolean;
  enableResponseTimeReports: boolean;
  enableCallReports: boolean;
  enableAccountUtilizationReports: boolean;
  enableAgentAvailabilityReports: boolean;
  enableAgentUtilizationReports: boolean;
  enableIvrReports: boolean;
}

const DEFAULT_CONFIG: ReportSettingsConfig = {
  showAgentDashboard: true,
  allowAgentThresholds: true,
  allowAgentDashboards: true,
  allowSharedDashboards: true,
  showAdherenceToAgents: false,
};

type GuideSection = "overview" | "agent-dashboard" | "thresholds" | "dashboards" | "adherence" | "reports" | "export";

const GUIDE_SECTIONS: { id: GuideSection; title: string }[] = [
  { id: "overview", title: "Overview" },
  { id: "reports", title: "Available Reports" },
  { id: "agent-dashboard", title: "Agent Dashboard" },
  { id: "thresholds", title: "KPI Thresholds" },
  { id: "dashboards", title: "Custom Dashboards" },
  { id: "adherence", title: "Schedule Adherence" },
  { id: "export", title: "Export & Sharing" },
];

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`Toggle ${label}`}
      aria-disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500 ${
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
      } ${checked ? "bg-cornflower-blue-500" : "bg-slate-gray-300"}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function LockedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      Contact Velaro to enable
    </span>
  );
}

export default function ReportSettingsPanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const { displayToast } = useToastAlert();

  const [config, setConfig] = useState<ReportSettingsConfig>(DEFAULT_CONFIG);
  const [subFlags, setSubFlags] = useState<SubscriptionFlags | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [guideSection, setGuideSection] = useState<GuideSection>("overview");

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/report-settings");
      if (resp.ok) {
        const data = await resp.json();
        if (data.config) setConfig({ ...DEFAULT_CONFIG, ...data.config });
        if (data.subscription) setSubFlags(data.subscription);
      }
    } catch {
      // safe defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  function setField(key: keyof ReportSettingsConfig, value: boolean) {
    setConfig((c) => ({ ...c, [key]: value }));
    setDirty(true);
  }

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const resp = await api.messaging.post("admin/report-settings", config);
      if (resp.ok) {
        displayToast(Severity.Success, "Report settings saved.");
        setDirty(false);
      } else {
        displayToast(Severity.Error, "Failed to save report settings.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to save report settings.");
    } finally {
      setSaving(false);
    }
  }, [api.messaging, config, displayToast]);

  // Count enabled reports for summary
  const enabledReports = subFlags
    ? [
        subFlags.enableConversationReports,
        subFlags.enableMissedChatReports,
        subFlags.enableSentimentReports,
        subFlags.enableAgentPerformanceReports,
        subFlags.enableResponseTimeReports,
        subFlags.enableCallReports,
        subFlags.enableAccountUtilizationReports,
        subFlags.enableAgentAvailabilityReports,
        subFlags.enableAgentUtilizationReports,
        subFlags.enableIvrReports,
        subFlags.enableScheduleAdherenceReports,
      ].filter(Boolean).length
    : 0;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-slate-400 text-sm">Loading report settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-gray-800">Reports & Analytics</h2>
          <p className="text-sm text-slate-gray-500 mt-1">
            Configure how reporting features work for your agents. Features must first be enabled
            in your subscription plan — settings here control agent-level behavior.
          </p>
        </div>
        <button
          onClick={() => setShowGuide(!showGuide)}
          className="shrink-0 px-3 py-1.5 text-xs font-medium text-cornflower-blue-600 bg-cornflower-blue-50 rounded-lg hover:bg-cornflower-blue-100 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500"
          aria-expanded={showGuide}
          aria-controls="report-guide"
        >
          {showGuide ? "Hide Guide" : "Feature Guide"}
        </button>
      </div>

      {/* Expandable Guide */}
      {showGuide && (
        <div id="report-guide" className="bg-white rounded-lg border border-slate-gray-200">
          <div className="flex">
            {/* Sidebar nav */}
            <nav className="w-44 shrink-0 border-r border-slate-gray-200 p-3">
              <div className="space-y-0.5">
                {GUIDE_SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setGuideSection(s.id)}
                    className={`block w-full text-left px-3 py-1.5 rounded text-xs transition ${
                      guideSection === s.id
                        ? "bg-cornflower-blue-50 text-cornflower-blue-700 font-medium"
                        : "text-slate-gray-600 hover:bg-slate-gray-100"
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            </nav>

            {/* Content */}
            <div className="flex-1 p-5 text-sm text-slate-gray-700 leading-relaxed">
              {guideSection === "overview" && <GuideOverview />}
              {guideSection === "reports" && <GuideReports subFlags={subFlags} />}
              {guideSection === "agent-dashboard" && <GuideAgentDashboard />}
              {guideSection === "thresholds" && <GuideThresholds />}
              {guideSection === "dashboards" && <GuideDashboards />}
              {guideSection === "adherence" && <GuideAdherence />}
              {guideSection === "export" && <GuideExport />}
            </div>
          </div>
        </div>
      )}

      {/* Summary card */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-3">Your Plan</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-3 rounded-lg bg-slate-gray-50">
            <div className="text-2xl font-bold text-cornflower-blue-600">{enabledReports}</div>
            <div className="text-xs text-slate-gray-500 mt-1">Report Types</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-slate-gray-50">
            <div className={`text-2xl font-bold ${subFlags?.enableAgentDashboard ? "text-success-green-600" : "text-slate-gray-300"}`}>
              {subFlags?.enableAgentDashboard ? "On" : "Off"}
            </div>
            <div className="text-xs text-slate-gray-500 mt-1">Agent Dashboard</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-slate-gray-50">
            <div className={`text-2xl font-bold ${subFlags?.enableCustomDashboards ? "text-success-green-600" : "text-slate-gray-300"}`}>
              {subFlags?.enableCustomDashboards ? "On" : "Off"}
            </div>
            <div className="text-xs text-slate-gray-500 mt-1">Custom Dashboards</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-slate-gray-50">
            <div className={`text-2xl font-bold ${subFlags?.enableCustomThresholds ? "text-success-green-600" : "text-slate-gray-300"}`}>
              {subFlags?.enableCustomThresholds ? "On" : "Off"}
            </div>
            <div className="text-xs text-slate-gray-500 mt-1">Custom Thresholds</div>
          </div>
        </div>
      </div>

      {/* Agent Dashboard settings */}
      <FeatureSection
        title="Agent Personal Dashboard"
        description="When enabled, agents see their personal performance metrics (conversations handled, response time, resolution rate) on the idle screen when no conversation is selected."
        enabled={subFlags?.enableAgentDashboard ?? false}
      >
        <SettingRow
          label="Show dashboard on idle screen"
          description="Display the personal metrics dashboard when agents have no active conversation selected."
          checked={config.showAgentDashboard}
          onChange={(v) => setField("showAgentDashboard", v)}
          disabled={!subFlags?.enableAgentDashboard}
        />
      </FeatureSection>

      {/* KPI Thresholds settings */}
      <FeatureSection
        title="KPI Color Thresholds"
        description="Color-coded performance indicators on report cards. When enabled, agents can customize their own green/yellow/red thresholds for metrics like occupancy, availability, CSAT, and response time."
        enabled={subFlags?.enableCustomThresholds ?? false}
      >
        <SettingRow
          label="Allow agent threshold customization"
          description="Let individual agents adjust their own KPI color thresholds. When off, all agents see the industry-standard defaults."
          checked={config.allowAgentThresholds}
          onChange={(v) => setField("allowAgentThresholds", v)}
          disabled={!subFlags?.enableCustomThresholds}
        />
      </FeatureSection>

      {/* Custom Dashboards settings */}
      <FeatureSection
        title="Custom KPI Dashboards"
        description="Agents can build personalized dashboards with the metrics that matter most to them. Dashboards display real-time KPI cards that auto-refresh every 30 seconds."
        enabled={subFlags?.enableCustomDashboards ?? false}
      >
        <SettingRow
          label="Allow agents to create dashboards"
          description="Let agents build and manage their own custom KPI dashboards from the Reports section."
          checked={config.allowAgentDashboards}
          onChange={(v) => setField("allowAgentDashboards", v)}
          disabled={!subFlags?.enableCustomDashboards}
        />
        <SettingRow
          label="Allow shared dashboards"
          description="Let agents share their dashboards with other agents on the same site. Shared dashboards appear in everyone's dashboard list."
          checked={config.allowSharedDashboards}
          onChange={(v) => setField("allowSharedDashboards", v)}
          disabled={!subFlags?.enableCustomDashboards}
        />
        {subFlags?.enableCustomDashboards && (subFlags?.maxCustomDashboards ?? 0) > 0 && (
          <div className="px-5 py-3 text-xs text-slate-gray-500 border-t border-slate-100">
            Dashboard limit: {subFlags.maxCustomDashboards} per site (contact Velaro to increase)
          </div>
        )}
      </FeatureSection>

      {/* Schedule Adherence settings */}
      <FeatureSection
        title="Schedule Adherence"
        description="Tracks how well agents follow their assigned work schedules. Compares actual availability times against scheduled shifts to calculate adherence percentages."
        enabled={subFlags?.enableScheduleAdherenceReports ?? false}
      >
        <SettingRow
          label="Show adherence data to agents"
          description="Allow agents to see their own schedule adherence metrics. When off, only admins and managers can view adherence reports."
          checked={config.showAdherenceToAgents}
          onChange={(v) => setField("showAdherenceToAgents", v)}
          disabled={!subFlags?.enableScheduleAdherenceReports}
        />
      </FeatureSection>

      {/* How it works */}
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-5">
        <h3 className="font-semibold text-cornflower-blue-800 mb-2">How permissions work</h3>
        <ul className="text-sm text-cornflower-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">1.</span>
            <span>Your <strong>subscription plan</strong> determines which report features are available. Locked features show a badge.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">2.</span>
            <span>As an admin, you control <strong>agent-level access</strong> using the toggles above. These settings apply to all agents on your site.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">3.</span>
            <span>Changes take effect immediately for all agents on their next page load.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">4.</span>
            <span>Report data is visible to Administrators and Managers by default. Use the settings above to extend visibility to agents.</span>
          </li>
        </ul>
      </div>

      {/* Save button */}
      {dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-cornflower-blue-500 text-white rounded-lg text-sm font-medium hover:bg-cornflower-blue-600 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Wraps a feature section with subscription gating */
function FeatureSection({
  title,
  description,
  enabled,
  children,
}: {
  title: string;
  description: string;
  enabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`bg-white rounded-lg border ${enabled ? "border-slate-gray-200" : "border-slate-gray-100"}`}>
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-1">
          <h3 className={`font-semibold ${enabled ? "text-slate-gray-800" : "text-slate-gray-400"}`}>{title}</h3>
          {!enabled && <LockedBadge />}
        </div>
        <p className={`text-sm mt-0.5 ${enabled ? "text-slate-gray-500" : "text-slate-gray-400"}`}>{description}</p>
      </div>
      {enabled && <div className="border-t border-slate-100 divide-y divide-slate-100">{children}</div>}
    </div>
  );
}

/** A single toggle row within a feature section */
function SettingRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-5 py-4">
      <div>
        <div className="font-medium text-slate-gray-800 text-sm">{label}</div>
        <div className="text-xs text-slate-gray-500 mt-0.5">{description}</div>
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} disabled={disabled} />
    </div>
  );
}

/* ============================
   Guide Sections
   ============================ */

function GuideOverview() {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        Reports & Analytics Overview
      </h3>
      <p>
        The reporting suite provides real-time and historical analytics across all your communication channels.
        Reports help you understand conversation volume, agent performance, customer satisfaction, and workforce utilization.
      </p>
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-3 text-xs text-cornflower-blue-800">
        <strong className="uppercase text-[10px] tracking-wide">How It Works</strong>
        <div className="mt-1">
          Your subscription plan determines which reports are available. As an admin, you control additional features
          like agent dashboards, custom thresholds, and custom dashboards using the settings on this page. Use the
          sections in this guide to learn about each feature.
        </div>
      </div>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Key Concepts</h4>
      <table className="w-full text-xs border border-slate-gray-200 rounded overflow-hidden">
        <thead>
          <tr className="bg-slate-gray-50">
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Concept</th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-3 py-2 border-b border-slate-100">Overview</td><td className="px-3 py-2 border-b border-slate-100">Summary KPI cards and charts for a report type</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Breakdown</td><td className="px-3 py-2 border-b border-slate-100">Detailed per-agent, per-day data table with sorting and filtering</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Export</td><td className="px-3 py-2 border-b border-slate-100">Download report data as CSV for external analysis</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Thresholds</td><td className="px-3 py-2 border-b border-slate-100">Color-coded KPI indicators (green/yellow/red)</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Teams</td><td className="px-3 py-2 border-b border-slate-100">Filter report data by team/department</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function GuideReports({ subFlags }: { subFlags: SubscriptionFlags | null }) {
  const reports = [
    { name: "Conversations", key: "enableConversationReports" as keyof SubscriptionFlags, desc: "Volume, status, and duration metrics for all conversations across channels" },
    { name: "Missed Chats", key: "enableMissedChatReports" as keyof SubscriptionFlags, desc: "Conversations that were abandoned or timed out before an agent responded" },
    { name: "Satisfaction / Sentiment", key: "enableSentimentReports" as keyof SubscriptionFlags, desc: "AI-powered analysis of customer sentiment and satisfaction trends" },
    { name: "Agent Performance", key: "enableAgentPerformanceReports" as keyof SubscriptionFlags, desc: "Per-agent leaderboards: conversations handled, resolution rates, response times" },
    { name: "Response Time", key: "enableResponseTimeReports" as keyof SubscriptionFlags, desc: "First response time tracking and SLA compliance monitoring" },
    { name: "Calls / IVR", key: "enableCallReports" as keyof SubscriptionFlags, desc: "Call volume, duration, outcome, and IVR flow analytics" },
    { name: "Account Utilization", key: "enableAccountUtilizationReports" as keyof SubscriptionFlags, desc: "Executive dashboard with cross-metric trends and daily volumes" },
    { name: "Agent Availability", key: "enableAgentAvailabilityReports" as keyof SubscriptionFlags, desc: "Timeline view of agent statuses and total availability hours" },
    { name: "Agent Utilization", key: "enableAgentUtilizationReports" as keyof SubscriptionFlags, desc: "Workforce analytics: occupancy, staffing levels, team KPIs" },
    { name: "IVR Reports", key: "enableIvrReports" as keyof SubscriptionFlags, desc: "IVR-specific call flow analytics and voice interaction reports" },
    { name: "Schedule Adherence", key: "enableScheduleAdherenceReports" as keyof SubscriptionFlags, desc: "Compares agent actual availability against assigned schedules" },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        Available Reports
      </h3>
      <p>Reports included in your current plan are marked with a green checkmark.</p>
      <div className="space-y-1">
        {reports.map((r) => {
          const enabled = subFlags ? Boolean(subFlags[r.key]) : false;
          return (
            <div key={r.key} className={`flex items-start gap-3 p-2.5 rounded-lg ${enabled ? "" : "opacity-50"}`}>
              <span className={`mt-0.5 ${enabled ? "text-success-green-600" : "text-slate-gray-300"}`}>
                {enabled ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                )}
              </span>
              <div>
                <div className={`text-xs font-medium ${enabled ? "text-slate-gray-800" : "text-slate-gray-400"}`}>{r.name}</div>
                <div className="text-[11px] text-slate-gray-500">{r.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GuideAgentDashboard() {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        Agent Personal Dashboard
      </h3>
      <p>
        The agent dashboard displays personal performance metrics on the idle screen (when no conversation is selected).
        This gives agents immediate visibility into their own KPIs without navigating to reports.
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">What Agents See</h4>
      <table className="w-full text-xs border border-slate-gray-200 rounded overflow-hidden">
        <thead>
          <tr className="bg-slate-gray-50">
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Metric</th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-3 py-2 border-b border-slate-100">Conversations Handled</td><td className="px-3 py-2 border-b border-slate-100">Total conversations the agent participated in during the selected period</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Avg Response Time</td><td className="px-3 py-2 border-b border-slate-100">Average time to first reply in seconds</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Resolution Rate</td><td className="px-3 py-2 border-b border-slate-100">Percentage of conversations resolved without transfer or escalation</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Availability</td><td className="px-3 py-2 border-b border-slate-100">Percentage of time the agent was in Available status</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Sentiment</td><td className="px-3 py-2 border-b border-slate-100">Breakdown of positive / neutral / negative customer sentiment</td></tr>
        </tbody>
      </table>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Time Periods</h4>
      <p>Agents can switch between Last 24 Hours, Last 7 Days, and Last 30 Days. The dashboard auto-refreshes every 60 seconds.</p>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
        <strong className="uppercase text-[10px] tracking-wide">Tip</strong>
        <div className="mt-1">
          The agent dashboard uses the same KPI color thresholds as reports. If custom thresholds are enabled,
          agents will see their personalized colors here too.
        </div>
      </div>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Admin Controls</h4>
      <p>
        Use the <strong>Show dashboard on idle screen</strong> toggle above to control whether agents see the dashboard.
        The feature must first be enabled in your subscription plan. When the toggle is off, agents see the standard
        idle screen with a conversation selection prompt.
      </p>
    </div>
  );
}

function GuideThresholds() {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        KPI Color Thresholds
      </h3>
      <p>
        Threshold settings control the green/yellow/red color coding on report KPI cards.
        By default, the system uses industry-standard thresholds. When custom thresholds are enabled,
        agents can personalize their own color boundaries.
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Industry-Standard Defaults</h4>
      <table className="w-full text-xs border border-slate-gray-200 rounded overflow-hidden">
        <thead>
          <tr className="bg-slate-gray-50">
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">KPI</th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">
              <span className="inline-block w-2 h-2 rounded-full bg-success-green-500 mr-1" aria-hidden="true" />Green
            </th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">
              <span className="inline-block w-2 h-2 rounded-full bg-sweet-orange-400 mr-1" aria-hidden="true" />Yellow
            </th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">
              <span className="inline-block w-2 h-2 rounded-full bg-crimson-red-500 mr-1" aria-hidden="true" />Red
            </th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-3 py-2 border-b border-slate-100">Occupancy</td><td className="px-3 py-2 border-b border-slate-100">75-85%</td><td className="px-3 py-2 border-b border-slate-100">85-90%</td><td className="px-3 py-2 border-b border-slate-100">&gt;90% or &lt;75%</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Availability</td><td className="px-3 py-2 border-b border-slate-100">&ge;70%</td><td className="px-3 py-2 border-b border-slate-100">40-69%</td><td className="px-3 py-2 border-b border-slate-100">&lt;40%</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">CSAT</td><td className="px-3 py-2 border-b border-slate-100">&ge;85%</td><td className="px-3 py-2 border-b border-slate-100">75-84%</td><td className="px-3 py-2 border-b border-slate-100">&lt;75%</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Resolution Rate</td><td className="px-3 py-2 border-b border-slate-100">&ge;80%</td><td className="px-3 py-2 border-b border-slate-100">70-79%</td><td className="px-3 py-2 border-b border-slate-100">&lt;70%</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Avg Response Time</td><td className="px-3 py-2 border-b border-slate-100">&lt;30s</td><td className="px-3 py-2 border-b border-slate-100">30-60s</td><td className="px-3 py-2 border-b border-slate-100">&gt;60s</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Schedule Adherence</td><td className="px-3 py-2 border-b border-slate-100">&ge;95%</td><td className="px-3 py-2 border-b border-slate-100">90-94%</td><td className="px-3 py-2 border-b border-slate-100">&lt;90%</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Abandonment Rate</td><td className="px-3 py-2 border-b border-slate-100">&lt;5%</td><td className="px-3 py-2 border-b border-slate-100">5-8%</td><td className="px-3 py-2 border-b border-slate-100">&gt;8%</td></tr>
        </tbody>
      </table>
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-3 text-xs text-cornflower-blue-800">
        <strong className="uppercase text-[10px] tracking-wide">Info</strong>
        <div className="mt-1">
          When custom thresholds are enabled, each agent can override these defaults from within any report page
          using the collapsible "Threshold Settings" panel. Their preferences are saved per-user and persist
          across sessions. Agents can reset to defaults at any time.
        </div>
      </div>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Admin Controls</h4>
      <p>
        Use the <strong>Allow agent threshold customization</strong> toggle to control whether agents can modify their
        own thresholds. When disabled, all agents see the industry-standard defaults shown above. The feature must
        first be enabled in your subscription plan.
      </p>
    </div>
  );
}

function GuideDashboards() {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        Custom KPI Dashboards
      </h3>
      <p>
        Custom dashboards let agents build personalized views with the metrics that matter most to their role.
        Each dashboard is a collection of KPI widgets that auto-refresh in real time.
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Available Widgets</h4>
      <table className="w-full text-xs border border-slate-gray-200 rounded overflow-hidden">
        <thead>
          <tr className="bg-slate-gray-50">
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Category</th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Widgets</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-3 py-2 border-b border-slate-100">Real-Time</td><td className="px-3 py-2 border-b border-slate-100">Active Conversations, Total Agents Online, Missed Rate, Avg Response Time</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Performance</td><td className="px-3 py-2 border-b border-slate-100">Resolution Rate, CSAT Score, Avg Occupancy, Avg Availability, Abandonment Rate, Adherence</td></tr>
        </tbody>
      </table>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Sharing</h4>
      <p>
        When shared dashboards are enabled, agents can mark their dashboards as "Shared" so other agents on the site
        can view them. Shared dashboards appear in everyone's dashboard list. Only the creator can edit or delete a
        shared dashboard.
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Default Dashboard</h4>
      <p>
        A dashboard can be marked as "Default" — when set, it appears on the agent idle screen below the personal
        metrics (if the agent dashboard is also enabled). This lets admins create a site-wide KPI view that all
        agents see without navigating to reports.
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Admin Controls</h4>
      <ul className="list-disc ml-5 space-y-1 text-xs">
        <li><strong>Allow agents to create dashboards</strong> — controls whether agents can build new dashboards</li>
        <li><strong>Allow shared dashboards</strong> — controls whether the "Share" option is available</li>
        <li>Your plan may include a limit on total dashboards per site</li>
      </ul>
    </div>
  );
}

function GuideAdherence() {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        Schedule Adherence
      </h3>
      <p>
        Schedule adherence compares agents' actual availability against their assigned work schedules.
        It answers the question: "Are agents online and available when they're supposed to be?"
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">How It's Calculated</h4>
      <table className="w-full text-xs border border-slate-gray-200 rounded overflow-hidden">
        <thead>
          <tr className="bg-slate-gray-50">
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Metric</th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Formula</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-3 py-2 border-b border-slate-100">Adherence %</td><td className="px-3 py-2 border-b border-slate-100">(Time in Available status during scheduled hours) / (Total scheduled hours) x 100</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Late Login</td><td className="px-3 py-2 border-b border-slate-100">Minutes between scheduled start and actual first Available status</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Early Logout</td><td className="px-3 py-2 border-b border-slate-100">Minutes between last Available status and scheduled end</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Overtime</td><td className="px-3 py-2 border-b border-slate-100">Time in Available status outside of scheduled hours</td></tr>
        </tbody>
      </table>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Prerequisites</h4>
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900">
        <strong className="uppercase text-[10px] tracking-wide">Important</strong>
        <div className="mt-1">
          Schedule adherence requires <strong>work schedules</strong> to be configured. Set up schedules and assign
          agents to them in the Admin section. Without assigned schedules, the adherence report will show a message
          prompting you to configure schedules first.
        </div>
      </div>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Admin Controls</h4>
      <p>
        Use the <strong>Show adherence data to agents</strong> toggle to control whether agents can see their own
        adherence metrics. By default, adherence data is only visible to Administrators and Managers in the Reports
        section. Enabling this lets agents self-monitor their schedule compliance.
      </p>
    </div>
  );
}

function GuideExport() {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-slate-gray-800 border-b border-slate-gray-200 pb-2">
        Export & Sharing
      </h3>
      <p>
        All report views support CSV export. Click the export button on any report page to download the
        current view's data as a CSV file for use in spreadsheets or external analytics tools.
      </p>
      <h4 className="text-sm font-semibold text-slate-gray-700 mt-3">Export Capabilities</h4>
      <table className="w-full text-xs border border-slate-gray-200 rounded overflow-hidden">
        <thead>
          <tr className="bg-slate-gray-50">
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Feature</th>
            <th className="text-left px-3 py-2 font-medium text-slate-gray-600 border-b border-slate-gray-200">Details</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-3 py-2 border-b border-slate-100">Format</td><td className="px-3 py-2 border-b border-slate-100">CSV (comma-separated values), compatible with Excel, Google Sheets, etc.</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Scope</td><td className="px-3 py-2 border-b border-slate-100">Exports the current date range and team filter selection</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Date Limit</td><td className="px-3 py-2 border-b border-slate-100">Maximum 90 days per export (configurable in your plan)</td></tr>
          <tr className="bg-slate-gray-50"><td className="px-3 py-2 border-b border-slate-100">Monthly Limit</td><td className="px-3 py-2 border-b border-slate-100">Your plan may include a monthly export cap — check your usage</td></tr>
          <tr><td className="px-3 py-2 border-b border-slate-100">Both Views</td><td className="px-3 py-2 border-b border-slate-100">Export is available on both Overview (summary) and Breakdown (detailed) pages</td></tr>
        </tbody>
      </table>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
        <strong className="uppercase text-[10px] tracking-wide">Tip</strong>
        <div className="mt-1">
          For recurring reports, consider using Report Email Subscriptions (if enabled in your plan) to receive
          automated report summaries by email on a schedule.
        </div>
      </div>
    </div>
  );
}
